import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // Import useLocation
import axios from 'axios';
import { API_URL } from '../../utils/config'; // Import API_URL
// import { AuthContext } from '../../context/AuthContext'; // Removed AuthContext import
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Rating, // Using MUI Rating component
  TextareaAutosize,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getImageSrc } from '../../utils/imageHelper'; // Assuming image helper exists

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
}));

const ProductReviewItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
    marginBottom: 0,
    paddingBottom: 0,
  },
}));

const ProductImage = styled('img')({
  width: 80,
  height: 80,
  objectFit: 'cover',
  marginRight: 16,
  borderRadius: 4,
});

const ReviewForm = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Use location to get query params

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const emailParam = queryParams.get('email');
  const orderIdParam = queryParams.get('order_id');

  // const { user, token } = useContext(AuthContext); // Removed user/token from context
  const [orderData, setOrderData] = useState(null);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({ firstname: '' });
  const [ratings, setRatings] = useState({}); // Store ratings like { productId: ratingValue }
  const [reviews, setReviews] = useState({}); // Store review text like { productId: reviewText }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Removed token check

    // Check if verification params are present in URL
    if (!emailParam || !orderIdParam) {
        setError('Invalid review link. Missing verification details.');
        setLoading(false);
        return;
    }

    // Define fetchReviewData inside useEffect or ensure it uses dependencies correctly
    const fetchReviewData = async () => {
      setLoading(true);
      setError('');
      try {
        // Pass email and order_id as query params, remove Authorization header
        const response = await axios.get(`${API_URL}/reviews/form/${orderNumber}`, {
            params: {
                email: emailParam,
                order_id: orderIdParam
            }
        });
        // Removed duplicate/incorrect fetch logic below

        // Process successful response
        if (response.data.success) {
          setOrderData(response.data.order);
          setProducts(response.data.products);
          // Cannot pre-fill firstname without login context
          setFormData({ firstname: '' }); // Initialize firstname as empty
          // Initialize ratings and reviews state
          const initialRatings = {};
          const initialReviews = {};
          response.data.products.forEach(p => {
            initialRatings[p.product_id] = null; // Initialize rating as null or 0
            initialReviews[p.product_id] = '';
          });
          setRatings(initialRatings);
          setReviews(initialReviews);
        } else {
          setError(response.data.message || 'Failed to load review data.');
        }
      } catch (err) {
        console.error("Error fetching review data:", err);
        setError(err.response?.data?.message || 'An error occurred while fetching review data.');
      } finally {
        setLoading(false);
      }
    }; // End of fetchReviewData function definition

    fetchReviewData();
    // Update dependencies array
  }, [orderNumber, emailParam, orderIdParam, navigate]); // Removed token, user

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRatingChange = (productId, newValue) => {
    setRatings({ ...ratings, [productId]: newValue });
  };

  const handleReviewTextChange = (productId, value) => {
    setReviews({ ...reviews, [productId]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    // Basic validation
    if (!formData.firstname.trim()) {
      setError('Please enter your first name.');
      setIsSubmitting(false);
      return;
    }

    const missingRating = products.some(p => ratings[p.product_id] === null || ratings[p.product_id] === 0);
    if (missingRating) {
      setError('Please provide a rating for all products.');
      setIsSubmitting(false);
      return;
    }

    // Prepare submission data
    const submissionData = { firstname: formData.firstname };
    products.forEach(p => {
      submissionData[`rating${p.product_id}`] = ratings[p.product_id];
      submissionData[`review${p.product_id}`] = reviews[p.product_id];
    });

    try {
      // Pass email and order_id as query params, remove Authorization header
      const response = await axios.post(`${API_URL}/reviews/${orderNumber}`, submissionData, {
          params: {
              email: emailParam,
              order_id: orderIdParam
          }
      });
      // Removed old config/call
      // Removed old config/call

      if (response.data.success) {
        setSuccess(response.data.message + (response.data.coupon_code ? ` Your coupon code is: ${response.data.coupon_code}` : ''));
        // Optionally disable form or redirect
        // navigate('/my-orders'); // Example redirect
      } else {
        setError(response.data.message || 'Failed to submit review.');
      }
    } catch (err) {
      console.error("Error submitting review:", err);
      setError(err.response?.data?.message || 'An error occurred while submitting your review.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
  }

  if (error && !orderData) { // Show error only if loading failed completely
    return <Container maxWidth="md"><Alert severity="error" sx={{ mt: 3 }}>{error}</Alert></Container>;
  }

  if (!orderData) { // Should not happen if error handling is correct, but as a fallback
      return <Container maxWidth="md"><Alert severity="warning" sx={{ mt: 3 }}>Could not load review information.</Alert></Container>;
  }

  return (
    <Container maxWidth="md">
      <StyledPaper elevation={3}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Leave a Review
        </Typography>
        <Typography variant="h6" gutterBottom align="center">
          Order: #{orderData?.order_number}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {!success && ( // Hide form after successful submission
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="firstname"
              label="Your First Name"
              name="firstname"
              value={formData.firstname}
              onChange={handleInputChange}
              autoFocus
              sx={{ mb: 2 }}
            />

            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
              Rate Your Products:
            </Typography>

            {products.map((product) => (
              <ProductReviewItem key={product.product_id}>
                <ProductImage
                  src={getImageSrc(product.product_image, 'thumbs')} // Defaults to .webp
                  alt={product.product_name}
                  onError={(e) => {
                    e.target.onerror = null; // Prevent infinite loop
                    // Try original format if webp fails
                    e.target.src = `https://storage.googleapis.com/eladore-ruffles.appspot.com/images/thumbs/${product.product_image}`; 
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" component="div" gutterBottom>
                    {product.product_name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                     <Typography component="legend" sx={{ mr: 1 }}>Rating:*</Typography>
                     <Rating
                       name={`rating-${product.product_id}`} // Unique name for accessibility
                       value={ratings[product.product_id] || 0}
                       onChange={(event, newValue) => {
                         handleRatingChange(product.product_id, newValue);
                       }}
                     />
                  </Box>
                  <TextareaAutosize
                    aria-label={`Review for ${product.product_name}`}
                    minRows={3}
                    placeholder="Share your thoughts... (Optional)"
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', borderColor: '#ccc', fontFamily: 'inherit' }}
                    value={reviews[product.product_id] || ''}
                    onChange={(e) => handleReviewTextChange(product.product_id, e.target.value)}
                  />
                </Box>
              </ProductReviewItem>
            ))}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || loading}
                sx={{ minWidth: 150 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Submit Review'}
              </Button>
            </Box>
          </Box>
        )}
      </StyledPaper>
    </Container>
  );
};

export default ReviewForm;
